/* Capa 1 · el tren: un vagón por día. Se desliza en horizontal; el del centro manda la cabecera.
   Tocar un vagón abre su ventanilla hasta llenar la pantalla. */
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useApuntes } from '../datos/apuntes.ts';
import { progresoCasillas } from './Documento.tsx';
import type { PestanaGuia } from '../hojas/Guia.tsx';
import type { Dia, Viaje } from '../datos/tipos.ts';
import { claveDe } from '../escena/bloques.ts';
import { cresta, HITOS, terreno } from '../escena/dibujo.ts';
import { reducido, type Motor } from '../escena/motor.ts';
import { paleta, varsDe } from '../escena/paleta.ts';
import { azar, f1 } from '../escena/util.ts';
import { CIUDADES, esc, fechaCorta, fechaLarga, fechaSello, kanjiDe, num2, titulo } from '../texto.ts';
import { ahora, ahoraMin, conSimulador, simulado, simular, useSimulado } from '../tiempo.ts';

interface Props {
  motor: Motor;
  v: Viaje;
  activa: boolean;
  hoy: number;
  desde: number;                    // el vagón que se centra al llegar
  tamano: string;
  onEntrar: (n: number, ventana: DOMRect) => void;
  onGuia: (p: PestanaGuia) => void;
  onCuanto: () => void;
}

const morro = (lado: 'izq' | 'der') => `<svg class="morro morro--${lado}" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true"><path d="M100,0 L100,100 L22,100 Q0,99 2,88 Q10,52 58,14 Q76,0 100,0 Z" fill="#f2f0ea"/><path d="M100,78 L100,88 L8,88 Q6,83 9,78 Z" fill="var(--linea)"/><path d="M100,18 L66,21 Q48,31 40,44 L100,42 Z" fill="#2c3240"/></svg>`;

function sello(d: Dia) {
  const giro = ((d.n * 37) % 24) - 12;
  return `<svg class="eki-sello" viewBox="0 0 64 64" aria-hidden="true" style="transform:rotate(${giro}deg)">
    <g fill="none" stroke="#c9321e" filter="url(#tinta)"><circle cx="32" cy="32" r="29" stroke-width="2.5"/><circle cx="32" cy="32" r="24.5" stroke-width="1"/><line x1="12" y1="41" x2="52" y2="41" stroke-width="1"/></g>
    <g fill="#c9321e" filter="url(#tinta)" font-family="Zen Kaku Gothic New, sans-serif" text-anchor="middle">
      <text x="32" y="16.5" font-size="6" font-weight="700">JP${num2(d.n)}</text>
      <text x="32" y="36" font-size="15" font-weight="900">${kanjiDe(d)}</text>
      <text x="32" y="50" font-size="6.5" font-weight="700">${esc(fechaSello(d))}</text>
    </g></svg>`;
}

/** La ventanilla pequeña de cada vagón: el monumento del día a su hora. */
function miniEscena(dia: Dia) {
  const b = claveDe(dia), pal = paleta(b.t.ini + 20), W = 240, H = 130, base = H * 0.74, r = azar(dia.n * 31);
  let est = '';
  if (+pal.noche > 0.4) for (let k = 0; k < 18; k++) est += `<circle cx="${f1(r() * W)}" cy="${f1(r() * base * 0.7)}" r="${f1(0.4 + r())}" fill="#fff" opacity=".8"/>`;
  return `<svg viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMax slice" style="${varsDe(pal)}" aria-hidden="true">
    <defs><linearGradient id="mg${dia.n}" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${pal.arriba}"/><stop offset=".74" stop-color="${pal.abajo}"/></linearGradient></defs>
    <rect width="${W}" height="${H}" fill="url(#mg${dia.n})"/>${est}
    ${cresta(-10, W + 10, base, 10, 26, r, 40, 'l')}
    <rect class="suelo" y="${base}" width="${W}" height="${H - base}"/>
    ${terreno(b.terreno, dia.ciudad, -10, W + 10, base, 0.45, r)}
    ${b.hito ? HITOS[b.hito](W / 2, base, 0.42, r) : ''}
  </svg>`;
}

/** «14/10/2026», «2026-10-14» → «14/10». */
const diaMes = (f: unknown) => {
  const s = String(f ?? '');
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  return iso ? `${iso[3]}/${iso[2]}` : s.slice(0, 5);
};

export function Tren({ motor, v, activa, hoy, desde, tamano, onEntrar, onGuia, onCuanto }: Props) {
  const dias = v.dias, total = dias.length;
  const convoy = useRef<HTMLDivElement>(null);
  const [centrado, setCentrado] = useState(desde);
  const [fase, setFase] = useState(0);
  const sim = useSimulado();
  const { sync, pendientes: porSubir } = useApuntes();
  const minis = useMemo(() => new Map(dias.map(d => [d.n, miniEscena(d)])), [dias]);

  const centrar = (n: number, suave = false) => {
    const el = convoy.current?.querySelector<HTMLElement>(`.vagon[data-n="${n}"]`);
    if (el && convoy.current) convoy.current.scrollTo({ left: el.offsetLeft + el.offsetWidth / 2 - convoy.current.clientWidth / 2, behavior: suave && !reducido() ? 'smooth' : 'instant' });
  };

  // Al llegar al tren: el paisaje de hoy detrás, a la hora de ahora, y el vagón del día centrado
  useLayoutEffect(() => {
    if (!activa) return;
    motor.modo = 'tren';
    motor.construir(dias[Math.min(total, Math.max(1, hoy)) - 1]);
    motor.p = ahoraMin();
    setCentrado(desde);
    centrar(desde);
    motor.pintar(motor.p, (convoy.current?.scrollLeft ?? 0) * 0.05);
    document.title = `Tabiji 旅路 · ${v.viaje.nombre}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activa, tamano, hoy, v]);

  // El rótulo LED alterna entre japonés y español
  useEffect(() => { const i = setInterval(() => setFase(f => f ^ 1), 3500); return () => clearInterval(i); }, []);

  const raf = useRef(0);
  const alDeslizar = () => {
    if (raf.current) return;
    raf.current = requestAnimationFrame(() => {
      raf.current = 0;
      const c = convoy.current!;
      const medio = c.scrollLeft + c.clientWidth / 2;
      let mejor: HTMLElement | null = null, dist = Infinity;
      for (const el of c.children as HTMLCollectionOf<HTMLElement>) { const d = Math.abs(el.offsetLeft + el.offsetWidth / 2 - medio); if (d < dist) { dist = d; mejor = el; } }
      if (mejor && +mejor.dataset.n! !== centrado) setCentrado(+mejor.dataset.n!);
      if (activa) motor.pintar(motor.p, c.scrollLeft * 0.05);
    });
  };

  const entrar = (n: number) => {
    const ventana = convoy.current?.querySelector(`.vagon[data-n="${n}"] .vagon__ventana`);
    if (ventana) onEntrar(n, ventana.getBoundingClientRect());
  };

  const d = dias[Math.min(total, Math.max(1, centrado)) - 1];
  const [grande, peq] = hoy < 1 ? [1 - hoy, <>días<br />para salir</>] : hoy > total ? [total, <>días<br />vividos</>] : [hoy, <>de {total}<br />días</>];
  const pendientes = v.reservas.filter(r => /PENDIENTE/.test(r.estado ?? ''));
  const preparar = progresoCasillas([v.documentos.preparativos, v.documentos.examenes]);
  const t = ahora();

  let ciudad: string | null = null;
  const trazado = dias.flatMap(x => {
    const piezas = [];
    if (x.ciudad !== ciudad) { ciudad = x.ciudad; piezas.push(<span key={`c${x.n}`} className="ciudad" lang="ja" aria-hidden="true" style={{ color: `var(--c-${x.ciudad})` }}>{CIUDADES[x.ciudad][0]}</span>); }
    piezas.push(<button key={x.n} type="button" style={{ '--linea': `var(--c-${x.ciudad})` } as CSSProperties} aria-label={`Día ${x.n}, ${fechaLarga(x)}`} aria-current={x.n === d.n} onClick={() => centrar(x.n, true)} />);
    return piezas;
  });

  return (
    <section className="tren" id="tren" aria-labelledby="tren-titulo" inert={!activa}>
      <header className="tren__cabeza">
        <p className="kicker">{v.viaje.nombre.toUpperCase()} · <span lang="ja">旅路</span></p>
        <h1 className="tren__titulo" id="tren-titulo"><span className="grande">{grande}</span><span className="pequeno">{peq}</span></h1>
        {hoy < 1 && pendientes.length > 0 && (
          <div className="led" role="group" aria-label="Por cerrar antes de salir">
            <div className="led__fila"><span>{fase ? 'POR CERRAR' : '未完了'}</span><span>{num2(t.getHours())}:{num2(t.getMinutes())}</span></div>
            <button type="button" className="led__fila led__boton" onClick={() => onGuia('antes')}>
              <span>{fase ? 'ANTES DE SALIR' : '準備'}</span><span>{preparar.hechas}/{preparar.total} ›</span>
            </button>
            {pendientes.map(r => (
              <button type="button" key={r.id} className="led__fila led__boton" style={{ color: '#fff3cf', marginTop: '.35rem' }} onClick={() => onGuia('antes')}>
                <span>{r.que.split(' · ')[0].slice(0, 22)}</span><span style={{ color: '#ffb000' }}>{diaMes(r.fecha)} ›</span>
              </button>
            ))}
          </div>
        )}
        <p className="tren__dia" aria-live="polite"><small>JP{num2(d.n)} · {fechaCorta(d).toUpperCase()}{d.n === hoy ? ' · HOY' : ''}</small>{titulo(d.titulo)}</p>
      </header>
      <div className="convoy" ref={convoy} role="list" aria-label={`Los ${total} días del viaje, desliza en horizontal`} tabIndex={0} onScroll={alDeslizar}
        onKeyDown={e => {
          // Con teclado: ← → cambian de vagón; Intro entra en el del centro
          if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') { e.preventDefault(); centrar(Math.min(total, Math.max(1, centrado + (e.key === 'ArrowRight' ? 1 : -1))), true); }
          if (e.key === 'Enter' && e.target === e.currentTarget) entrar(centrado);
        }}>
        {dias.map(x => {
          const pasado = x.n < hoy, esHoy = x.n === hoy;
          return (
            <div key={x.n} className={`vagon ${pasado ? 'is-pasado' : ''}`} role="listitem" data-n={x.n} style={{ '--linea': `var(--c-${x.ciudad})` } as CSSProperties}>
              {x.n === 1 && <span dangerouslySetInnerHTML={{ __html: morro('izq') }} style={{ display: 'contents' }} />}
              {x.n === total && <span dangerouslySetInnerHTML={{ __html: morro('der') }} style={{ display: 'contents' }} />}
              <button className="vagon__caja" type="button" aria-label={`Día ${x.n}, ${fechaLarga(x)}: ${x.titulo}${esHoy ? '. Hoy' : ''}${pasado ? '. Ya vivido' : ''}`} onClick={() => entrar(x.n)}>
                <span className="vagon__ventana" dangerouslySetInnerHTML={{ __html: minis.get(x.n)! }} />
                <span className="vagon__puerta" />
                <span className="vagon__franja"><span>JP{num2(x.n)}</span><span lang="ja">{kanjiDe(x)}</span></span>
              </button>
              <span className="vagon__bogies" aria-hidden="true"><i /><i /></span>
              {pasado && <span dangerouslySetInnerHTML={{ __html: sello(x) }} style={{ display: 'contents' }} />}
              {esHoy && <span className="vagon__pin">ESTÁS AQUÍ</span>}
              {hoy < 1 && x.n === 1 && <span className="vagon__pin">SALE EN {1 - hoy} {1 - hoy === 1 ? 'DÍA' : 'DÍAS'}</span>}
            </div>
          );
        })}
      </div>
      <footer className="tren__pie">
        <nav className="trazado" aria-label="Ir a un día">{trazado}</nav>
        <div className="tren__acciones">
          <button className="boton-entrar" type="button" onClick={() => entrar(d.n)}>{d.n === hoy ? 'Mirar hoy por la ventanilla →' : `Mirar el día ${d.n} →`}</button>
          <button className="pildora boton-guia" type="button" onClick={() => onGuia(hoy < 1 ? 'antes' : 'compras')}><span lang="ja" aria-hidden="true">案内</span>Guía</button>
          <button className="pildora boton-guia pildora--cuanto" type="button" onClick={onCuanto}><span lang="ja" aria-hidden="true">¥</span>¿Cuánto es?</button>
        </div>
        {sync === 'sin-red' && porSubir.length > 0 && <p className="sync" role="status">Sin conexión · {porSubir.length} {porSubir.length === 1 ? 'apunte espera' : 'apuntes esperan'} para subir</p>}
        {sync === 'error' && <p className="sync" data-estado="error" role="status">El servidor no acepta lo apuntado · se reintenta solo</p>}
        {conSimulador && (
          <label className="simular">Simular
            <select value={simulado() ?? 'real'} onChange={e => simular(e.target.value === 'real' ? null : e.target.value)}>
              <option value="real">hora real</option>
              {[sim, new URLSearchParams(location.search).get('sim'), '2026-10-12T15:05', '2026-10-17T18:40', '2026-10-21T21:30', '2026-10-24T09:20']
                .filter((x, i, a) => x && a.indexOf(x) === i)
                .map(x => <option key={x} value={x!}>{new Date(x!).toLocaleString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</option>)}
            </select>
          </label>
        )}
      </footer>
    </section>
  );
}
